/**
 * parseUrl — extract an archive.org item identifier from a pasted URL.
 *
 * Handles all URL shapes defined in spec FR-001:
 *   - https://archive.org/details/{id}
 *   - https://archive.org/embed/{id}
 *   - https://archive.org/download/{id}/...
 *   - https://archive.org/stream/{id}/...
 *   - https://{node}.us.archive.org/.../items/{id}/...
 *
 * FR-004: when the pasted input is free text that contains a URL among other
 * content, extract the first valid URL and parse it.
 *
 * Returns null for any non-archive.org input.
 */

const ID_RE = /^[a-zA-Z0-9._-]+$/;
const URL_IN_TEXT_RE = /https?:\/\/[^\s"'<>()]+/i;

const PATH_SHAPES = [
  /^\/(?:details|embed|download|stream)\/([^/?#]+)/,
  /^\/(?:[^/]+\/)*items\/([^/?#]+)/,
];

export function parseUrl(input: string): string | null {
  if (!input) return null;

  // FR-004: if free text, pull out the first http(s) URL.
  let candidate = input.trim();
  if (!/^https?:\/\//i.test(candidate)) {
    const match = candidate.match(URL_IN_TEXT_RE);
    if (!match) return null;
    candidate = match[0];
  }

  let u: URL;
  try {
    u = new URL(candidate);
  } catch {
    return null;
  }

  const host = u.hostname.toLowerCase();
  const isArchiveHost =
    host === "archive.org" ||
    host === "www.archive.org" ||
    host.endsWith(".archive.org");
  if (!isArchiveHost) return null;

  for (const shape of PATH_SHAPES) {
    const m = u.pathname.match(shape);
    if (m && m[1]) {
      const id = decodeURIComponent(m[1]);
      if (ID_RE.test(id)) return id;
    }
  }

  return null;
}
